import type { ChartConfig } from "@/components/ui/chart";

export const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
];

const MAX_SAMPLE_ROWS = 50;
const CATEGORICAL_DISTINCT_RATIO = 0.3;

/**
 * Detects which result columns are numeric vs categorical by sampling multiple rows.
 * A column is numeric only if the majority of its non-empty values parse as numbers
 * AND it doesn't look like an ID/categorical column (low distinct-value ratio).
 * @param rows - The parsed result row data (all values are strings from CSV)
 * @param columns - The column names
 * @returns An object with category and numeric column arrays
 */
export function detectColumnTypes(
  rows: Array<Record<string, string>>,
  columns: string[],
): { category: string[]; numeric: string[] } {
  const numeric: string[] = [];
  const category: string[] = [];
  const sampleRows = rows.slice(0, MAX_SAMPLE_ROWS);

  for (const col of columns) {
    const nonEmptyValues = sampleRows
      .map((r) => r[col])
      .filter((v) => v !== "" && v !== undefined);

    if (nonEmptyValues.length === 0) {
      category.push(col);
      continue;
    }

    const numericCount = nonEmptyValues.filter((v) => !isNaN(Number(v))).length;
    const isNumeric = numericCount / nonEmptyValues.length > 0.8;

    if (!isNumeric) {
      category.push(col);
      continue;
    }

    // Columns with very few distinct values relative to row count likely represent
    // categories (e.g. IDs, status codes) rather than continuous measures.
    const distinctValues = new Set(nonEmptyValues);
    const distinctRatio = distinctValues.size / nonEmptyValues.length;
    const allIntegers = nonEmptyValues.every((v) =>
      Number.isInteger(Number(v)),
    );
    const looksLikeId =
      allIntegers &&
      distinctRatio > 0.9 &&
      nonEmptyValues.length > 3 &&
      col.toLowerCase().includes("id");

    if (
      looksLikeId ||
      (allIntegers &&
        distinctRatio <= CATEGORICAL_DISTINCT_RATIO &&
        nonEmptyValues.length > 5)
    ) {
      category.push(col);
    } else {
      numeric.push(col);
    }
  }

  return { category, numeric };
}

/**
 * Builds the recharts-compatible ChartConfig from the y-axis column list.
 * @param yAxes - The y-axis column names
 * @returns A ChartConfig mapping each column to a label and color
 */
export function buildChartConfig(yAxes: string[]): ChartConfig {
  const config: ChartConfig = {};
  yAxes.forEach((col, i) => {
    config[col] = {
      label: col,
      color: CHART_COLORS[i % CHART_COLORS.length],
    };
  });
  return config;
}

/**
 * Converts parsed CSV rows into recharts-compatible data, coercing y-axis values to numbers.
 * @param rows - The parsed CSV rows (all string values)
 * @param columns - All column names
 * @param yAxes - The y-axis column names (will be coerced to numeric, defaulting to 0)
 * @returns Array of data entries with numeric y-axis values
 */
export function buildChartData(
  rows: Array<Record<string, string>>,
  columns: string[],
  yAxes: string[],
): Array<Record<string, unknown>> {
  const yAxisSet = new Set(yAxes);
  return rows.map((row) => {
    const entry: Record<string, unknown> = {};
    for (const col of columns) {
      const value = row[col];
      const num = Number(value);
      if (value !== "" && !isNaN(num)) {
        entry[col] = num;
      } else if (yAxisSet.has(col)) {
        entry[col] = 0;
      } else {
        entry[col] = value;
      }
    }
    return entry;
  });
}
